import React, { useEffect, useState } from "react";
import { Alert, Button, ButtonGroup, Card, CardBody, Col, Container, CustomInput, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { KINDS, loadListState, saveListState } from "../../helpers/partnerReports";
import { PartnerAccess } from "./PartnerAccess";
import { QontoClientPicker } from "./QontoClientPicker";

// Reportings partenaires : destinataires, langue, rythmes (mensuel / trimestriel / annuel, chacun avec PDF), source des chiffres (TRC ou Kwanko).
// Deux types : distributeurs (clé = marchand) et marques (clé brand~<marque>, chiffres TRC seulement).
const EMPTY = { partnerType: "merchant", brandName: "", merchantKey: "", market: "fr", displayName: "", to: [], cc: [], language: "fr", monthly: true, quarterly: true, yearly: true, enabled: true, dataSource: "trc", kwankoCampaignId: 0, accentColor: "#e11d48", logoUrl: "" };

const lines = (s) => (s || "").split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
const merchantName = (m) => m.name || m.Name || "";
const merchantLogo = (m) => m.s3LogoUrl || m.S3LogoUrl || m.logoUrl || m.LogoUrl || "";
const brandName = (b) => b.name || b.Name || "";
const brandLogo = (b) => b.s3LogoUrl || b.S3LogoUrl || b.logo || b.Logo || b.logoUrl || b.LogoUrl || "";
const typeOf = (c) => c.partnerType || "merchant";

export function Configs() {
    const [list, setList] = useState(null);
    const [merchants, setMerchants] = useState([]);
    const [brands, setBrands] = useState([]);
    const [type, setTypeState] = useState(() => loadListState("").type); // même onglet que la page Rapports
    const setType = (t) => { setTypeState(t); saveListState({ ...loadListState(""), type: t }); };
    const [edit, setEdit] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => apiFetch(API.back + "/partner-report-configs", { market: false })
        .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
        .then(setList)
        .catch((e) => { setError("Configurations indisponibles : " + e.message); setList([]); });

    useEffect(() => {
        load();
        apiFetch(API.products + "/merchants", { market: false }).then((r) => r.json()).then((d) => setMerchants(Array.isArray(d) ? d : [])).catch(() => setMerchants([]));
        apiFetch(API.products + "/brands", { market: false }).then((r) => r.json()).then((d) => setBrands(Array.isArray(d) ? d : [])).catch(() => setBrands([]));
    }, []);

    const open = (cfg) => setEdit({ ...EMPTY, ...cfg, toText: (cfg?.to || []).join("\n"), ccText: (cfg?.cc || []).join("\n"),
        portalEnabled: !!cfg?.portalEnabled, domainsText: (cfg?.portalDomains || []).join("\n"),
        qontoClientId: cfg?.qontoClientId || "", qontoClientName: cfg?.qontoClientName || "" });
    const set = (patch) => setEdit((e) => ({ ...e, ...patch }));

    const pickMerchant = (name) => {
        const m = merchants.find((x) => merchantName(x) === name);
        set({ merchantKey: name, displayName: edit.displayName || name, logoUrl: edit.logoUrl || (m ? merchantLogo(m) : "") });
    };

    const pickBrand = (name) => {
        const b = brands.find((x) => brandName(x) === name);
        set({ brandName: name, displayName: edit.displayName || name, logoUrl: edit.logoUrl || (b ? brandLogo(b) : "") });
    };

    const isBrand = edit && edit.partnerType === "brand";
    const shown = (list || []).filter((c) => typeOf(c) === type);

    const save = async () => {
        setSaving(true);
        setError(null);
        const { toText, ccText, domainsText, ...cfg } = edit;
        const body = { ...cfg, to: lines(toText), cc: lines(ccText), kwankoCampaignId: Number(cfg.kwankoCampaignId) || 0,
            portalEnabled: !!cfg.portalEnabled, portalDomains: lines(domainsText),
            qontoClientId: cfg.qontoClientId || "", qontoClientName: cfg.qontoClientId ? cfg.qontoClientName : "" };
        const res = await apiFetch(API.back + "/partner-report-config", { method: "PUT", market: false, body: JSON.stringify(body) });
        setSaving(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setEdit(null);
        load();
    };

    const remove = async (c) => {
        if (!window.confirm(`Supprimer la configuration ${c.displayName} (${c.market.toUpperCase()}) ? Les rapports déjà générés restent consultables.`)) return;
        const res = await apiFetch(`${API.back}/partner-report-config?merchantKey=${encodeURIComponent(c.merchantKey)}&market=${c.market}`, { method: "DELETE", market: false });
        if (!res.ok) { setError(await readError(res)); return; }
        load();
    };

    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h3 mb-0">{type === "brand" ? "Marques du reporting" : "Distributeurs du reporting"}</h1>
                <div className="d-flex align-items-center">
                    <ButtonGroup className="mr-2">
                        <Button color={type === "merchant" ? "primary" : "light"} onClick={() => setType("merchant")}>Distributeurs</Button>
                        <Button color={type === "brand" ? "primary" : "light"} onClick={() => setType("brand")}>Marques</Button>
                    </ButtonGroup>
                    <Button color="primary" onClick={() => open({ partnerType: type })}>{type === "brand" ? "+ Ajouter une marque" : "+ Ajouter un distributeur"}</Button>
                </div>
            </div>
            {error && !edit && <Alert color="danger">{error}</Alert>}
            <Card>
                <CardBody className="p-0">
                    {!list ? <div className="p-3"><Spinner size="sm" /></div> : shown.length === 0 ? <div className="p-3">{type === "brand" ? "Aucune marque configurée." : "Aucun distributeur configuré."}</div> : (
                        <Table className="mb-0" hover responsive>
                            <thead><tr><th>Partenaire</th><th>Marché</th><th>Destinataires</th><th>Rythmes</th><th>Source</th><th>Actif</th><th /></tr></thead>
                            <tbody>
                                {shown.map((c) => (
                                    <tr key={c.merchantKey + c.market}>
                                        <td><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: c.accentColor, marginRight: 8 }} /><b>{c.displayName}</b>{typeOf(c) === "brand" && <div className="small text-muted">{c.brandName}</div>}</td>
                                        <td>{c.market.toUpperCase()}</td>
                                        <td className="small">{(c.to || []).join(", ") || <span className="text-danger">aucun</span>}{(c.cc || []).length > 0 && <div className="text-muted">Cc : {c.cc.join(", ")}</div>}</td>
                                        <td>{KINDS.filter(([id]) => c[id]).map(([, label]) => label).join(" + ") || "—"}</td>
                                        <td>{typeOf(c) !== "brand" && c.dataSource === "kwanko" ? `Kwanko (${c.kwankoCampaignId})` : "TRC"}</td>
                                        <td>{c.enabled ? "Oui" : "Non"}</td>
                                        <td className="text-right text-nowrap">
                                            <Button size="sm" color="light" className="mr-1" onClick={() => open(c)}>Modifier</Button>
                                            <Button size="sm" color="link" className="text-danger" onClick={() => remove(c)}>Supprimer</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {edit && (
                <Modal isOpen toggle={() => setEdit(null)} size="lg">
                    <ModalHeader toggle={() => setEdit(null)}>{edit.updatedAt ? `Modifier ${edit.displayName}` : isBrand ? "Nouvelle marque" : "Nouveau distributeur"}</ModalHeader>
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        <Row form>
                            {isBrand ? <Col md={6}><FormGroup><Label>Marque (nom du catalogue)</Label>
                                <Input type="select" value={edit.brandName} onChange={(e) => pickBrand(e.target.value)} disabled={!!edit.updatedAt}>
                                    <option value="">— choisir —</option>
                                    {edit.brandName && !brands.some((b) => brandName(b) === edit.brandName) && <option value={edit.brandName}>{edit.brandName}</option>}
                                    {brands.map(brandName).filter(Boolean).sort((a, b) => a.localeCompare(b)).map((n) => <option key={n} value={n}>{n}</option>)}
                                </Input></FormGroup></Col> :
                            <Col md={6}><FormGroup><Label>Marchand</Label>
                                <Input type="select" value={edit.merchantKey} onChange={(e) => pickMerchant(e.target.value)} disabled={!!edit.updatedAt}>
                                    <option value="">— choisir —</option>
                                    {edit.updatedAt && <option value={edit.merchantKey}>{edit.merchantKey}</option>}
                                    {merchants.map((m) => merchantName(m)).filter(Boolean).sort((a, b) => a.localeCompare(b)).map((n) => <option key={n} value={n}>{n}</option>)}
                                </Input></FormGroup></Col>}
                            <Col md={3}><FormGroup><Label>Marché</Label>
                                <Input type="select" value={edit.market} onChange={(e) => set({ market: e.target.value, language: e.target.value })} disabled={!!edit.updatedAt}>
                                    <option value="fr">France</option><option value="es">Espagne</option>
                                </Input></FormGroup></Col>
                            <Col md={3}><FormGroup><Label>Langue du rapport</Label>
                                <Input type="select" value={edit.language} onChange={(e) => set({ language: e.target.value })}>
                                    <option value="fr">Français</option><option value="es">Espagnol</option>
                                </Input></FormGroup></Col>
                        </Row>
                        <FormGroup><Label>Nom affiché</Label><Input value={edit.displayName} onChange={(e) => set({ displayName: e.target.value })} /></FormGroup>
                        <Row form>
                            <Col md={6}><FormGroup><Label>À (une adresse par ligne)</Label><Input type="textarea" rows={3} value={edit.toText} onChange={(e) => set({ toText: e.target.value })} /></FormGroup></Col>
                            <Col md={6}><FormGroup><Label>Cc</Label><Input type="textarea" rows={3} value={edit.ccText} onChange={(e) => set({ ccText: e.target.value })} /></FormGroup></Col>
                        </Row>
                        <Row form className="mb-2">
                            {KINDS.map(([id, label]) => <Col md={3} key={id}><CustomInput type="switch" id={`pr-${id}`} label={label} checked={!!edit[id]} onChange={(e) => set({ [id]: e.target.checked })} /></Col>)}
                            <Col md={3}><CustomInput type="switch" id="pr-enabled" label="Actif" checked={edit.enabled} onChange={(e) => set({ enabled: e.target.checked })} /></Col>
                        </Row>
                        <Row form>
                            {!isBrand && <Col md={4}><FormGroup><Label>Source des clics et ventes</Label>
                                <Input type="select" value={edit.dataSource} onChange={(e) => set({ dataSource: e.target.value })}>
                                    <option value="trc">The Running Collective</option><option value="kwanko">Kwanko</option>
                                </Input></FormGroup></Col>}
                            {!isBrand && edit.dataSource === "kwanko" && <Col md={4}><FormGroup><Label>N° de campagne Kwanko</Label><Input type="number" value={edit.kwankoCampaignId || ""} onChange={(e) => set({ kwankoCampaignId: e.target.value })} /></FormGroup></Col>}
                            <Col md={isBrand ? 12 : edit.dataSource === "kwanko" ? 4 : 8}><FormGroup><Label>Couleur d'accent</Label><Input type="color" value={edit.accentColor} onChange={(e) => set({ accentColor: e.target.value })} style={{ maxWidth: 90 }} /></FormGroup></Col>
                        </Row>
                        <FormGroup><Label>Logo (https)</Label><Input value={edit.logoUrl} onChange={(e) => set({ logoUrl: e.target.value })} placeholder="https://…" />
                            {edit.logoUrl && <img src={edit.logoUrl} alt="" style={{ height: 32, marginTop: 8 }} />}</FormGroup>
                        <hr />
                        <h5 className="mb-2">Espace partenaires (réservation des displays)</h5>
                        <CustomInput type="switch" id="portal-on" checked={!!edit.portalEnabled} onChange={(e) => set({ portalEnabled: e.target.checked })} label="Accès ouvert (couper = toutes les sessions tombent)" />
                        <FormGroup className="mt-2"><Label>Domaines email autorisés (un par ligne, ex. i-run.fr)</Label>
                            <Input type="textarea" rows={2} value={edit.domainsText} onChange={(e) => set({ domainsText: e.target.value })} /></FormGroup>
                        <FormGroup className="mt-2"><Label>Client Qonto (devis et factures)</Label>
                            <QontoClientPicker value={edit.qontoClientId} name={edit.qontoClientName} onChange={(id, name) => set({ qontoClientId: id, qontoClientName: name })} /></FormGroup>
                        {edit.updatedAt ? <PartnerAccess partnerKey={edit.merchantKey} market={edit.market} />
                            : <p className="small text-muted mb-0">Enregistre d'abord le partenaire pour inviter des personnes.</p>}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setEdit(null)}>Annuler</Button>
                        <Button color="primary" onClick={save} disabled={saving || (isBrand ? !edit.brandName : !edit.merchantKey)}>{saving ? <Spinner size="sm" /> : "Enregistrer"}</Button>
                    </ModalFooter>
                </Modal>
            )}
        </Container>
    );
}
